import { CardHeader, Typography } from '@mui/material'
import { ReactElement } from 'react'
import { Feature_Base_Bool_Exp, Feature_Sorting_Config_Bool_Exp } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { resolveDetailsRoot } from 'src/routing/routing-utils'
import { FeatureBase } from 'src/screens/shared/feature/FeatureBase'
import { ProjectFeatureKpiGrid } from 'src/screens/shared/project/details/project-features/ProjectFeatureKpiGrid'
import { ProjectFeaturesYearlyKpiGrid } from 'src/screens/shared/project/details/project-features/ProjectFeaturesYearlyKpiGrid'
import { usePermissionsForProject } from 'src/service/security/PermissionHook'
import { EditButton } from 'src/shared/button/Buttons'
import { PROJECT } from 'src/shared/constants/constants'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { ProjectExportMenu } from 'src/shared/menu/ProjectExportMenu'
import { ExpandableCard } from 'src/shared/presentation/ExpandableCard'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { Section } from 'src/shared/presentation/Section'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { Utils } from 'src/shared/utils/Utils'

interface ProjectFeaturesProps {
  projectId: number
  baseUrl: '/pf-kap' | '/pf-pgv'
}

export const ProjectFeatures = ({ projectId, baseUrl }: ProjectFeaturesProps): ReactElement => {
  const navigate = useDelayedNavigate()
  const process = Utils.resolveProcess(baseUrl)

  const { getMessage } = useMessageSource()
  const {
    loading,
    canEdit,
    metadata: { projectBaseId, dossierStatus },
  } = usePermissionsForProject(process, projectId)

  const handleEditFeatureTypeSection = (featureTypeId: number) => {
    navigate(resolveDetailsRoot(baseUrl).nested.ProjectFeaturesEdit.params({ projectId, featureTypeId }))
  }

  const handleEditKPIs = () => {
    navigate(resolveDetailsRoot(baseUrl).nested.ProjectFeaturesKpiEdit.params({ projectId }))
  }

  const projectPfKapBaseWhereClause: Feature_Base_Bool_Exp = {
    project_base: {
      pf_kap_projects: {
        id: {
          _eq: projectId,
        },
      },
    },
  }

  const projectPfPgvBaseWhereClause: Feature_Base_Bool_Exp = {
    project_base: {
      pf_pgv_projects: {
        id: {
          _eq: projectId,
        },
      },
    },
  }

  const featureConfigSortingWhere: Feature_Sorting_Config_Bool_Exp = {
    process: {
      _eq: process,
    },
  }

  return (
    <>
      {!loading && (
        <ScreenLayout
          title={getMessage('label.navigation.project.details')}
          actions={<ProjectExportMenu process={process} entityId={projectBaseId as number} />}
        >
          <PageLayout>
            <>
              <FeatureBase
                featureBaseWhere={
                  process === PROJECT.PF_KAP ? projectPfKapBaseWhereClause : projectPfPgvBaseWhereClause
                }
                featureSortingConfigWhere={featureConfigSortingWhere}
                onEditFeature={handleEditFeatureTypeSection}
                canEdit={canEdit}
                kpiSection={
                  <>
                    <Section
                      async
                      title={getMessage('label.kpis')}
                      actionButton={<EditButton onClick={handleEditKPIs} hidden={!canEdit} />}
                    >
                      <ProjectFeatureKpiGrid targetEditable={false} projectId={projectId} baseUrl={baseUrl} />
                    </Section>

                    {dossierStatus && Utils.isDossierStatusAfterOrImplementation(dossierStatus) && (
                      <Section
                        title={getMessage('label.project.kpi.report.per.year.title')}
                        helpAndInstructions={
                          <HelpAndInstructions labelKey={`label.project.${process}.kpi.help.report.per.year`} />
                        }
                      >
                        <ExpandableCard
                          header={
                            <CardHeader
                              title={
                                <Typography variant="h6">
                                  {getMessage('label.project.kpi.report.per.year.title')}
                                </Typography>
                              }
                            />
                          }
                          content={<ProjectFeaturesYearlyKpiGrid projectId={projectId} baseUrl={baseUrl} />}
                        />
                      </Section>
                    )}
                  </>
                }
              />
            </>
          </PageLayout>
        </ScreenLayout>
      )}
    </>
  )
}
